// src/features/notification/notificationReducer.js

const initialState = {
  // Notification data
  notifications: [],
  unreadCount: 0,

  // Loading states
  loading: {
    notifications: false,
    unreadCount: false,
    markingRead: false,
    markingAllRead: false,
    deleting: false,
    preferences: false,
  },

  // Error states
  error: {
    notifications: null,
    unreadCount: null,
    markingRead: null,
    markingAllRead: null,
    deleting: null,
    preferences: null,
  },

  // Pagination
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },

  // Filters and sorting
  filters: {
    type: null, // 'follow' | null
    read: null, // true | false | null
    sort: "createdAt:desc",
  },

  // Selection for bulk operations
  selectedNotifications: [],

  // Preferences
  preferences: {
    follow: true,
  },

  // Real-time updates
  lastFetch: null,
  pollingEnabled: true,
};

function notificationReducer(state = initialState, action) {
  switch (action.type) {
    // Fetch notifications
    case "FETCH_NOTIFICATIONS_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          notifications: true,
        },
        error: {
          ...state.error,
          notifications: null,
        },
      };

    case "FETCH_NOTIFICATIONS_SUCCESS":
      return {
        ...state,
        loading: {
          ...state.loading,
          notifications: false,
        },
        error: {
          ...state.error,
          notifications: null,
        },
        notifications: action.payload.append
          ? [...state.notifications, ...action.payload.notifications]
          : action.payload.notifications,
        pagination: action.payload.pagination,
        lastFetch: Date.now(),
      };

    case "FETCH_NOTIFICATIONS_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          notifications: false,
        },
        error: {
          ...state.error,
          notifications: action.payload,
        },
      };

    // Fetch unread count
    case "FETCH_UNREAD_COUNT_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          unreadCount: true,
        },
        error: {
          ...state.error,
          unreadCount: null,
        },
      };

    case "FETCH_UNREAD_COUNT_SUCCESS":
      return {
        ...state,
        loading: {
          ...state.loading,
          unreadCount: false,
        },
        error: {
          ...state.error,
          unreadCount: null,
        },
        unreadCount: action.payload,
      };

    case "FETCH_UNREAD_COUNT_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          unreadCount: false,
        },
        error: {
          ...state.error,
          unreadCount: action.payload,
        },
      };

    // Mark notification as read
    case "MARK_NOTIFICATION_READ_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingRead: true,
        },
        error: {
          ...state.error,
          markingRead: null,
        },
      };

    case "MARK_NOTIFICATION_READ_SUCCESS":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingRead: false,
        },
        error: {
          ...state.error,
          markingRead: null,
        },
        notifications: state.notifications.map((notification) =>
          notification._id === action.payload.notificationId
            ? { ...notification, read: true }
            : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };

    case "MARK_NOTIFICATION_READ_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingRead: false,
        },
        error: {
          ...state.error,
          markingRead: action.payload,
        },
      };

    // Mark all notifications as read
    case "MARK_ALL_NOTIFICATIONS_READ_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingAllRead: true,
        },
        error: {
          ...state.error,
          markingAllRead: null,
        },
      };

    case "MARK_ALL_NOTIFICATIONS_READ_SUCCESS":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingAllRead: false,
        },
        error: {
          ...state.error,
          markingAllRead: null,
        },
        notifications: state.notifications.map((notification) => ({
          ...notification,
          read: true,
        })),
        unreadCount: 0,
      };

    case "MARK_ALL_NOTIFICATIONS_READ_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingAllRead: false,
        },
        error: {
          ...state.error,
          markingAllRead: action.payload,
        },
      };

    // Mark multiple notifications as read
    case "MARK_MULTIPLE_NOTIFICATIONS_READ_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingRead: true,
        },
        error: {
          ...state.error,
          markingRead: null,
        },
      };

    case "MARK_MULTIPLE_NOTIFICATIONS_READ_SUCCESS":
      const markedIds = action.payload.notificationIds;
      const unreadMarkedCount = state.notifications.filter(
        (n) => markedIds.includes(n._id) && !n.read
      ).length;

      return {
        ...state,
        loading: {
          ...state.loading,
          markingRead: false,
        },
        error: {
          ...state.error,
          markingRead: null,
        },
        notifications: state.notifications.map((notification) =>
          markedIds.includes(notification._id)
            ? { ...notification, read: true }
            : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - unreadMarkedCount),
        selectedNotifications: [],
      };

    case "MARK_MULTIPLE_NOTIFICATIONS_READ_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          markingRead: false,
        },
        error: {
          ...state.error,
          markingRead: action.payload,
        },
      };

    // Delete notification
    case "DELETE_NOTIFICATION_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          deleting: true,
        },
        error: {
          ...state.error,
          deleting: null,
        },
      };

    case "DELETE_NOTIFICATION_SUCCESS":
      const deletedNotification = state.notifications.find(
        (n) => n._id === action.payload.notificationId
      );

      return {
        ...state,
        loading: {
          ...state.loading,
          deleting: false,
        },
        error: {
          ...state.error,
          deleting: null,
        },
        notifications: state.notifications.filter(
          (notification) => notification._id !== action.payload.notificationId
        ),
        unreadCount:
          deletedNotification && !deletedNotification.read
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
        pagination: {
          ...state.pagination,
          total: Math.max(0, state.pagination.total - 1),
        },
      };

    case "DELETE_NOTIFICATION_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          deleting: false,
        },
        error: {
          ...state.error,
          deleting: action.payload,
        },
      };

    // Delete multiple notifications
    case "DELETE_MULTIPLE_NOTIFICATIONS_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          deleting: true,
        },
        error: {
          ...state.error,
          deleting: null,
        },
      };

    case "DELETE_MULTIPLE_NOTIFICATIONS_SUCCESS":
      const deletedIds = action.payload.notificationIds;
      const deletedUnreadCount = state.notifications.filter(
        (n) => deletedIds.includes(n._id) && !n.read
      ).length;

      return {
        ...state,
        loading: {
          ...state.loading,
          deleting: false,
        },
        error: {
          ...state.error,
          deleting: null,
        },
        notifications: state.notifications.filter(
          (notification) => !deletedIds.includes(notification._id)
        ),
        unreadCount: Math.max(0, state.unreadCount - deletedUnreadCount),
        selectedNotifications: [],
        pagination: {
          ...state.pagination,
          total: Math.max(0, state.pagination.total - deletedIds.length),
        },
      };

    case "DELETE_MULTIPLE_NOTIFICATIONS_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          deleting: false,
        },
        error: {
          ...state.error,
          deleting: action.payload,
        },
      };

    // Notification preferences
    case "FETCH_NOTIFICATION_PREFERENCES_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          preferences: true,
        },
        error: {
          ...state.error,
          preferences: null,
        },
      };

    case "FETCH_NOTIFICATION_PREFERENCES_SUCCESS":
      return {
        ...state,
        loading: {
          ...state.loading,
          preferences: false,
        },
        error: {
          ...state.error,
          preferences: null,
        },
        preferences: action.payload,
      };

    case "FETCH_NOTIFICATION_PREFERENCES_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          preferences: false,
        },
        error: {
          ...state.error,
          preferences: action.payload,
        },
      };

    case "UPDATE_NOTIFICATION_PREFERENCES_START":
      return {
        ...state,
        loading: {
          ...state.loading,
          preferences: true,
        },
        error: {
          ...state.error,
          preferences: null,
        },
      };

    case "UPDATE_NOTIFICATION_PREFERENCES_SUCCESS":
      return {
        ...state,
        loading: {
          ...state.loading,
          preferences: false,
        },
        error: {
          ...state.error,
          preferences: null,
        },
        preferences: action.payload,
      };

    case "UPDATE_NOTIFICATION_PREFERENCES_ERROR":
      return {
        ...state,
        loading: {
          ...state.loading,
          preferences: false,
        },
        error: {
          ...state.error,
          preferences: action.payload,
        },
      };

    // Filters and pagination
    case "SET_NOTIFICATION_FILTERS":
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
        pagination: {
          ...state.pagination,
          page: 1, // Reset to first page when filters change
        },
      };

    case "SET_NOTIFICATION_PAGE":
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: action.payload,
        },
      };

    // Selection for bulk operations
    case "SELECT_NOTIFICATION":
      const isSelected = state.selectedNotifications.includes(action.payload);
      return {
        ...state,
        selectedNotifications: isSelected
          ? state.selectedNotifications.filter((id) => id !== action.payload)
          : [...state.selectedNotifications, action.payload],
      };

    case "SELECT_ALL_NOTIFICATIONS":
      const allIds = state.notifications.map((n) => n._id);
      return {
        ...state,
        selectedNotifications: action.payload ? allIds : [],
      };

    case "CLEAR_NOTIFICATION_SELECTION":
      return {
        ...state,
        selectedNotifications: [],
      };

    // Real-time updates
    case "ADD_NEW_NOTIFICATION":
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1,
        pagination: {
          ...state.pagination,
          total: state.pagination.total + 1,
        },
      };

    case "UPDATE_NOTIFICATION":
      return {
        ...state,
        notifications: state.notifications.map((notification) =>
          notification._id === action.payload._id
            ? { ...notification, ...action.payload }
            : notification
        ),
      };

    case "REMOVE_NOTIFICATION":
      const removedNotification = state.notifications.find(
        (n) => n._id === action.payload
      );

      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification._id !== action.payload
        ),
        unreadCount:
          removedNotification && !removedNotification.read
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
        pagination: {
          ...state.pagination,
          total: Math.max(0, state.pagination.total - 1),
        },
      };

    // Polling control
    case "SET_POLLING_ENABLED":
      return {
        ...state,
        pollingEnabled: action.payload,
      };

    // Clear errors
    case "CLEAR_NOTIFICATION_ERROR":
      return {
        ...state,
        error: {
          ...state.error,
          [action.payload]: null,
        },
      };

    case "CLEAR_ALL_NOTIFICATION_ERRORS":
      return {
        ...state,
        error: {
          notifications: null,
          unreadCount: null,
          markingRead: null,
          markingAllRead: null,
          deleting: null,
          preferences: null,
        },
      };

    // Reset state
    case "RESET_NOTIFICATION_STATE":
      return initialState;

    default:
      return state;
  }
}

export default notificationReducer;
